// src/pages/Users/Techniques/hooks/useTechniquesController.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getAllTechniques } from "../../../../services/techniquesServices";

import {
  DEFAULT_TECHNIQUES_STATE,
  buildShareUrl,
  buildTechniqueOptions,
  filterAndSortTechniques,
} from "../utils/techniquesUtils";

const STORAGE_KEY = "ligue_na_techniques_ui_v2";

function normalizeTechniquesResponse(response) {
  if (Array.isArray(response)) {
    return response.filter(Boolean);
  }

  if (Array.isArray(response?.data)) {
    return response.data.filter(Boolean);
  }

  if (Array.isArray(response?.techniques)) {
    return response.techniques.filter(Boolean);
  }

  return [];
}

function readStoredState() {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);

    if (!storedValue) {
      return DEFAULT_TECHNIQUES_STATE;
    }

    const parsedValue = JSON.parse(storedValue);

    return {
      ...DEFAULT_TECHNIQUES_STATE,
      ...parsedValue,
      perPage: DEFAULT_TECHNIQUES_STATE.perPage,
    };
  } catch {
    return DEFAULT_TECHNIQUES_STATE;
  }
}

function readInitialState() {
  const storedState = readStoredState();

  if (typeof window === "undefined") {
    return storedState;
  }

  const params = new URLSearchParams(
    window.location.search,
  );

  return {
    ...storedState,
    search:
      params.get("q") !== null
        ? params.get("q")
        : storedState.search,
    grade:
      params.get("grade") ||
      storedState.grade,
    family:
      params.get("famille") ||
      storedState.family,
    sort:
      params.get("sort") ||
      storedState.sort,
    perPage: DEFAULT_TECHNIQUES_STATE.perPage,
  };
}

function saveState(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        search: state.search,
        grade: state.grade,
        family: state.family,
        sort: state.sort,
      }),
    );
  } catch {
    // Le stockage local reste facultatif.
  }
}

function copyTextFallback(text) {
  const textarea = document.createElement("textarea");

  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";

  document.body.appendChild(textarea);

  textarea.focus();
  textarea.select();

  document.execCommand("copy");

  document.body.removeChild(textarea);
}

export default function useTechniquesController() {
  const [allTechniques, setAllTechniques] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [reloadKey, setReloadKey] =
    useState(0);

  const [ui, setUi] =
    useState(readInitialState);

  const [searchInput, setSearchInput] =
    useState(ui.search || "");

  const [shareCopied, setShareCopied] =
    useState(false);

  useEffect(() => {
    let mounted = true;

    async function fetchTechniques() {
      setLoading(true);
      setError("");

      try {
        const response =
          await getAllTechniques();

        if (!mounted) {
          return;
        }

        setAllTechniques(
          normalizeTechniquesResponse(response),
        );
      } catch (fetchError) {
        console.error(
          "Erreur lors du chargement des techniques :",
          fetchError,
        );

        if (!mounted) {
          return;
        }

        setAllTechniques([]);

        setError(
          fetchError?.response?.data?.message ||
            fetchError?.message ||
            "Une erreur est survenue pendant le chargement.",
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchTechniques();

    return () => {
      mounted = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setUi((previousState) => {
        if (
          previousState.search === searchInput
        ) {
          return previousState;
        }

        const nextState = {
          ...previousState,
          search: searchInput,
          perPage:
            DEFAULT_TECHNIQUES_STATE.perPage,
        };

        saveState(nextState);

        return nextState;
      });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  const dynamicOptions = useMemo(
    () =>
      buildTechniqueOptions(allTechniques),
    [allTechniques],
  );

  const filteredTechniques = useMemo(
    () =>
      filterAndSortTechniques(
        allTechniques,
        ui,
      ),
    [allTechniques, ui],
  );

  const visibleTechniques = useMemo(
    () =>
      filteredTechniques.slice(
        0,
        ui.perPage,
      ),
    [filteredTechniques, ui.perPage],
  );

  const updateUi = useCallback((patch) => {
    setUi((previousState) => {
      const modifiesFilters = Object.keys(
        patch,
      ).some((key) =>
        ["grade", "family", "sort"].includes(
          key,
        ),
      );

      const nextState = {
        ...previousState,
        ...patch,
        perPage:
          patch.perPage !== undefined
            ? patch.perPage
            : modifiesFilters
              ? DEFAULT_TECHNIQUES_STATE.perPage
              : previousState.perPage,
      };

      saveState(nextState);

      return nextState;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setUi(DEFAULT_TECHNIQUES_STATE);
    setSearchInput(
      DEFAULT_TECHNIQUES_STATE.search,
    );

    saveState(DEFAULT_TECHNIQUES_STATE);
  }, []);

  const loadMore = useCallback(() => {
    setUi((previousState) => ({
      ...previousState,
      perPage:
        previousState.perPage +
        DEFAULT_TECHNIQUES_STATE.perPage,
    }));
  }, []);

  const copyShareLink = useCallback(async () => {
    try {
      const shareUrl = buildShareUrl(ui);

      if (
        navigator?.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(
          shareUrl,
        );
      } else {
        copyTextFallback(shareUrl);
      }

      setShareCopied(true);

      window.setTimeout(() => {
        setShareCopied(false);
      }, 1800);
    } catch (copyError) {
      console.error(
        "Impossible de copier le lien :",
        copyError,
      );
    }
  }, [ui]);

  const reloadTechniques = useCallback(() => {
    setReloadKey(
      (previousValue) =>
        previousValue + 1,
    );
  }, []);

  return {
    loading,
    error,
    allTechniques,
    filteredTechniques,
    visibleTechniques,
    dynamicOptions,
    ui,
    searchInput,
    shareCopied,
    setSearchInput,
    updateUi,
    resetFilters,
    loadMore,
    copyShareLink,
    reloadTechniques,
  };
}